import { useEffect, useRef, useState } from "react";
import { ArrowUp, Bot, Eraser, Sparkles, Square, UserRound, X } from "lucide-react";
import {
  AGENT_NOT_READY,
  CONVERSATION_RETENTION_LABEL,
  type AppliedChange,
} from "../../agent";
import {
  Button,
  Hint,
  IconButton,
  Notice,
  Textarea,
} from "../../../components/ui";
import { useAgentChat } from "../hooks/useAgentChat";
import { MarkdownText } from "./MarkdownText";
import "./AgentPanel.css";

type AgentPanelProps = {
  siteId: string;
  isSubscribed: boolean;
  subscribing: boolean;
  onSubscribe: () => void;
  onClose?: () => void;
  onApplied?: (changes: AppliedChange[]) => void | Promise<void>;
};

// Example prompts for the empty state; clicking one fills the input.
const EXAMPLE_PROMPTS = [
  {
    title: "Build me a photographer portfolio",
    body: "with a home page, an about page and a gallery grid",
  },
  {
    title: "Add an about page",
    body: "introduce me and what I make, then link it in the menu",
  },
  {
    title: "Refresh the homepage",
    body: "brighter opening line and a quote I would put on a poster",
  },
];

const roleBadgeClass: Record<string, string> = {
  user: "bg-hazumu-clay text-hazumu-ink",
  assistant: "bg-hazumu-terracotta text-hazumu-white",
  tool: "bg-hazumu-sage-pale text-hazumu-sage-deep",
};

const RetentionNote = () => (
  <p className="text-[11px] leading-4 text-hazumu-muted">
    {CONVERSATION_RETENTION_LABEL}
  </p>
);

const AppliedChanges = ({ changes }: { changes: AppliedChange[] }) =>
  changes.length ? (
    <ul className="mt-1.5 space-y-1">
      {changes.map((change, index) => (
        <li
          className="flex items-center gap-1.5 text-[11px] font-medium text-hazumu-sage-deep"
          key={`${change.tool}-${index}`}
        >
          <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-hazumu-sage" />
          {change.summary}
        </li>
      ))}
    </ul>
  ) : null;

type MessageBubbleProps = {
  role: "user" | "assistant" | "tool";
  content: string;
  changes: AppliedChange[];
};

const MessageBubble = ({ role, content, changes }: MessageBubbleProps) => {
  if (role === "tool") {
    return (
      <li className="flex justify-start">
        <div className="ml-8 rounded-2xl border border-hazumu-border bg-hazumu-canvas px-3 py-2">
          <AppliedChanges changes={changes} />
        </div>
      </li>
    );
  }
  const mine = role === "user";
  return (
    <li className={`flex items-start gap-2 ${mine ? "flex-row-reverse" : ""}`}>
      <span
        aria-hidden
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${mine ? roleBadgeClass.user : roleBadgeClass.assistant
          }`}
      >
        {mine ? <UserRound size={13} /> : <Bot size={13} />}
      </span>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-5 ${mine
          ? "whitespace-pre-wrap bg-hazumu-ink text-hazumu-white"
          : "border border-hazumu-border bg-hazumu-white text-hazumu-ink"
          }`}
      >
        {mine ? content : <MarkdownText content={content} />}
        <AppliedChanges changes={changes} />
      </div>
    </li>
  );
};

// Rotating status lines while a turn runs — the agent can take a while, so a
// little personality beats a bare spinner. Timing is deliberately slow
// enough to be readable and never lies about what's happening.
const WORKING_LINES = [
  "Ducky is thinking…",
  "Reading your site…",
  "Arranging your blocks…",
  "Choosing the words…",
  "Polishing the details…",
  "Tying up loose ends…",
  "Nearly there…",
];

const DuckyBadge = ({ tone }: { tone: "clay" | "terracotta" }) => (
  <span
    aria-hidden
    className={`relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-hazumu-white ${
      tone === "terracotta" ? "bg-hazumu-terracotta" : "bg-hazumu-clay text-hazumu-ink"
    }`}
  >
    <span
      className={`agent-halo absolute inset-0 rounded-full ${
        tone === "terracotta" ? "bg-hazumu-terracotta" : "bg-hazumu-clay"
      }`}
    />
    <Bot className="relative" size={13} />
  </span>
);

const WorkingBubble = ({ stopping }: { stopping: boolean }) => {
  const [line, setLine] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(
      () => setLine((current) => (current + 1) % WORKING_LINES.length),
      2_600,
    );
    return () => window.clearInterval(timer);
  }, []);
  return (
    <li
      className="flex items-center gap-2 text-sm text-hazumu-muted"
      role="status"
    >
      <span className="sr-only">Ducky is working</span>
      <DuckyBadge tone="terracotta" />
      <span aria-hidden className="agent-line" key={stopping ? "stopping" : line}>
        {stopping ? "Stopping…" : WORKING_LINES[line]}
      </span>
    </li>
  );
};

const EmptyState = ({ onPick }: { onPick: (prompt: string) => void }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 px-2 text-center">
    <span
      aria-hidden
      className="flex h-12 w-12 items-center justify-center rounded-2xl bg-hazumu-terracotta text-hazumu-white"
    >
      <Bot size={22} />
    </span>
    <div>
      <p className="font-semibold text-hazumu-ink">Build your site together</p>
      <Hint className="mt-1">
        Describe a change and Ducky edits the site with you. Try one of these:
      </Hint>
    </div>
    <ul className="w-full space-y-2">
      {EXAMPLE_PROMPTS.map((example) => (
        <li key={example.title}>
          <button
            className="w-full rounded-2xl border border-hazumu-border bg-hazumu-white px-4 py-3 text-left transition hover:bg-hazumu-canvas"
            onClick={() => onPick(`${example.title} — ${example.body}`)}
            type="button"
          >
            <span className="block text-sm font-semibold text-hazumu-ink">
              {example.title}
            </span>
            <span className="mt-0.5 block text-xs text-hazumu-muted">
              {example.body}
            </span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

export const AgentPanel = ({
  siteId,
  isSubscribed,
  subscribing,
  onSubscribe,
  onClose,
  onApplied,
}: AgentPanelProps) => {
  const {
    messages,
    loading,
    working,
    phase,
    clearing,
    error,
    runTurn,
    stop,
    clear,
  } = useAgentChat(siteId, isSubscribed, onApplied);
  const [input, setInput] = useState("");
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const didInitialScrollRef = useRef(false);

  const send = async () => {
    const text = input;
    if (!text.trim() || working) return;
    setInput("");
    await runTurn(text);
  };


  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const instant = !didInitialScrollRef.current || prefersReducedMotion;
    didInitialScrollRef.current = true;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: instant ? "auto" : "smooth",
    });
  }, [messages, working, loading]);

  return (
    <section
      aria-label="Ducky conversation"
      className="flex h-full min-h-0 flex-col gap-3"
    >
      <header className="flex items-center justify-between px-1">
        <div>
          <h2 className="font-lora text-xl font-semibold text-hazumu-ink">Ducky</h2>
          <p className="text-xs text-hazumu-muted">Edit your site by chatting</p>
        </div>
        <div className="flex items-center gap-1">
          {isSubscribed && messages.length > 0 && (
            <IconButton
              aria-label="Clear chat"
              disabled={working || clearing}
              onClick={() => void clear()}
              title="Clear chat"
            >
              <Eraser
                className={clearing ? "animate-pulse" : undefined}
                size={18}
              />
            </IconButton>
          )}
          {onClose && (
            <IconButton aria-label="Close Ducky" onClick={onClose}>
              <X size={18} />
            </IconButton>
          )}
        </div>
      </header>

      {loading ? (
        <div
          className="flex h-full items-center justify-center gap-2 text-sm text-hazumu-muted"
          role="status"
        >
          <span className="sr-only">Loading Ducky</span>
          <DuckyBadge tone="clay" />
          <span aria-hidden className="agent-line">Waking Ducky…</span>
        </div>
      ) : messages.length === 0 && !error && isSubscribed ? (
        <EmptyState onPick={setInput} />
      ) : (
        <ul
          aria-label="Messages"
          className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto pr-1"
          ref={scrollContainerRef}
        >
          {[...messages].map((message) => (
            <MessageBubble
            changes={message.changes}
            content={message.content}
            key={message.id}
            role={message.role}
            />
          ))}
          {working && <WorkingBubble stopping={phase === "stopping"} />}
        </ul>
      )}

      {phase === "cancelled" && (
        <Notice size="sm" tone="info">
          Stopped.
        </Notice>
      )}
      {phase === "failed" && (
        <Notice size="sm" tone="warning">
          Ducky couldn’t finish that. Anything it applied is saved — you can
          try again.
        </Notice>
      )}

      {error && (
        <Notice size="sm">
          {error === AGENT_NOT_READY
            ? "Ducky isn’t connected yet — your message is saved and will be answered once it goes live."
            : error}
        </Notice>
      )}

      {!isSubscribed ? (
        <Notice className="mt-auto" tone="info">
          <Hint>
            Ducky is included with Hazumu Pro. Subscribe to build your site by
            chatting.
          </Hint>
          <Button
            className="mt-3"
            disabled={subscribing}
            fullWidth
            onClick={onSubscribe}
            variant="accent"
          >
            <Sparkles size={16} />
            {subscribing ? "Opening checkout…" : "Subscribe to use Ducky"}
          </Button>
        </Notice>
      ) : (
        <div className="flex items-end gap-2">
          <Textarea
            aria-label="Message Ducky"
            className="resize-none"
            disabled={working}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Describe what you want to change…"
            rows={2}
            value={input}
          />
          {working ? (
            <Button
              aria-label={phase === "stopping" ? "Stopping" : "Stop generating"}
              className="self-stretch"
              disabled={phase === "stopping"}
              onClick={() => void stop()}
              variant="secondary"
            >
              <Square size={16} />
            </Button>
          ) : (
            <Button
              aria-label="Send message"
              className="self-stretch"
              disabled={!input.trim()}
              onClick={() => void send()}
              variant="primary"
            >
              <ArrowUp size={16} />
            </Button>
          )}
        </div>
      )}
      <RetentionNote />
    </section>
  );
};
